import React, { useState, useEffect, useCallback } from "react";
import { Layout, Modal, Form, Button, Tag, Divider, Typography, DatePicker } from "antd";
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  FileTextOutlined,
  PlusOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Api from "../api/Api";
import dayjs from "dayjs";

const { Content } = Layout;
const { RangePicker } = DatePicker;

/* ─── Status Config (ajustado para P, S, N) ─────────────────────────── */
const STATUS_CONFIG = {
  S: { bg: "#D1FAE5", color: "#065F46", dot: "#10B981", label: "Aprovada", tagColor: "success" },
  P: { bg: "#FEF3C7", color: "#92400E", dot: "#F59E0B", label: "Pendente", tagColor: "gold" },
  N: { bg: "#FEE2E2", color: "#991B1B", dot: "#EF4444", label: "Recusada", tagColor: "error" },
};

const SkeletonCard = () => (
  <div style={{ background: "#fff", border: "1px solid #F3F4F6", borderRadius: 14, padding: 18, marginBottom: 12 }}>
    {[80, 120, 60].map((w, i) => (
      <div
        key={i}
        style={{
          height: 12,
          width: `${w}%`,
          borderRadius: 6,
          marginBottom: 10,
          background: "linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)",
          backgroundSize: "200% 100%",
          animation: "shimmer 1.4s infinite",
        }}
      />
    ))}
  </div>
);

const DetailRow = ({ label, value, icon }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    {icon && <span style={{ color: "#9CA3AF", fontSize: 14 }}>{icon}</span>}
    <span style={{ fontSize: 13, color: "#6B7280", minWidth: 130 }}>{label}</span>
    <span style={{ fontSize: 13, fontWeight: 500, color: "#111827" }}>{value}</span>
  </div>
);

/* ─── Componente Principal ─────────────────────────────────────────────── */
const SolicitacaoFerias = () => {
  const userStored = JSON.parse(localStorage.getItem("user") || "{}");
  const employeeId = userStored.employee_id;
  const diretor = userStored.diretor || "N";
  const nomeUser = userStored.nome || userStored.name || "Colaborador";

  const [ferias, setFerias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDetalheOpen, setModalDetalheOpen] = useState(false);
  const [feriaSelecionada, setFeriaSelecionada] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [filtro, setFiltro] = useState("TODOS");
  const [form] = Form.useForm();

  useEffect(() => {
    document.title = "Férias | Dashboard";
  }, []);

  const loadFerias = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const res = await Api.ListFerias({ employee_id: employeeId });
      setFerias(Array.isArray(res) ? res : []);
    } catch {
      toast.error("Erro ao carregar férias.");
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    loadFerias();
  }, [loadFerias]);

  const feriasFiltradas = ferias.filter((f) => {
    if (filtro === "TODOS") return true;
    if (filtro === "APROVADO") return f.aprovado === "S";
    if (filtro === "PENDENTE") return f.aprovado === "P";
    if (filtro === "RECUSADO") return f.aprovado === "N";
    return true;
  });

  const submitFerias = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    const [inicio, fim] = values.periodo;
    const payload = {
      employee_id: employeeId,
      diretor,
      inicio: inicio.format("YYYY-MM-DD"),
      fim: fim.format("YYYY-MM-DD"),
      obs: values.obs || "",
      direcao: userStored.direcao,
    };
    try {
      await Api.AddFeria(payload);
      toast.success("Solicitação de férias enviada com sucesso!");
      setModalOpen(false);
      form.resetFields();
      loadFerias();
    } catch {
      toast.error("Erro ao enviar solicitação.");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusCfg = (aprovado) => STATUS_CONFIG[aprovado] || STATUS_CONFIG["P"];

  /* Stats */
  const stats = [
    { key: "TODOS", label: "Total", dot: "#6B7280", value: ferias.length },
    { key: "APROVADO", label: "Aprovadas", dot: "#10B981", value: ferias.filter((f) => f.aprovado === "S").length },
    {key: "PENDENTE",label: "Pendentes",dot: "#F59E0B",value: ferias.filter((f) => ["V", "P"].includes(f.aprovado)).length},
    { key: "RECUSADO", label: "Recusadas", dot: "#EF4444", value: ferias.filter((f) => f.aprovado === "N").length },
  ];

  return (
    <Layout style={{ background: "transparent", minHeight: "100vh" }}>
      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes fadeUp  { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:none} }
        .ferias-card { transition: all 0.18s; }
        .ferias-card:hover { transform: translateX(3px); border-color: #BFDBFE !important; box-shadow: 0 4px 16px rgba(0,0,0,0.08) !important; }
        .stat-chip { transition: all 0.18s; cursor: pointer; }
        .stat-chip:hover { transform: translateY(-2px); }
        .btn-novo:hover { opacity: 0.92; transform: translateY(-1px); }
      `}</style>

      <Content style={{ padding: "28px 36px", animation: "fadeUp 0.4s ease" }}>
        <ToastContainer position="top-right" autoClose={4000} />

        {/* ── HEADER ── */}
        <div
          style={{
            background: "linear-gradient(135deg, #0F4C8A 0%, #185FA5 50%, #2563EB 100%)",
            borderRadius: 18,
            padding: "26px 32px",
            marginBottom: 28,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: "0 16px 48px rgba(24,95,165,0.28)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div style={{ position: "absolute", right: -30, top: -30, width: 180, height: 180, borderRadius: "50%", background: "rgba(255,255,255,0.06)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", right: 60, bottom: -50, width: 120, height: 120, borderRadius: "50%", background: "rgba(255,255,255,0.04)", pointerEvents: "none" }} />

          <div style={{ position: "relative" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: "rgba(255,255,255,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg width="18" height="18" fill="none" stroke="#fff" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <h1 style={{ color: "#fff", fontSize: 20, fontWeight: 700, margin: 0, letterSpacing: "-0.3px" }}>
                Minhas Férias
              </h1>
            </div>
            <p style={{ color: "rgba(255,255,255,0.70)", fontSize: 13, margin: 0 }}>
              Olá, <strong style={{ color: "rgba(255,255,255,0.92)" }}>{nomeUser}</strong> — Gerencie as suas solicitações
            </p>
          </div>

          <button
            className="btn-novo"
            onClick={() => {
              form.resetFields();
              setModalOpen(true);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: "#fff",
              color: "#185FA5",
              border: "none",
              borderRadius: 12,
              padding: "11px 22px",
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
              transition: "all 0.2s",
            }}
          >
            <PlusOutlined /> Nova Solicitação
          </button>
        </div>

        {/* ── STATS / FILTROS ── */}
        <div style={{ display: "flex", gap: 10, marginBottom: 24, flexWrap: "wrap" }}>
          {stats.map((s) => (
            <div
              key={s.key}
              className="stat-chip"
              onClick={() => setFiltro(s.key)}
              style={{
                background: "#fff",
                border: `2px solid ${filtro === s.key ? s.dot : "#F3F4F6"}`,
                borderRadius: 14,
                padding: "14px 20px",
                flex: "1 1 80px",
                minWidth: 90,
                boxShadow: filtro === s.key ? `0 4px 16px ${s.dot}33` : "0 2px 8px rgba(0,0,0,0.04)",
                borderTop: `3px solid ${s.dot}`,
              }}
            >
              <div style={{ fontSize: 24, fontWeight: 700, color: s.dot, lineHeight: 1 }}>
                {loading ? "—" : s.value}
              </div>
              <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4, fontWeight: 500, letterSpacing: "0.2px" }}>
                {s.label}
              </div>
            </div>
          ))}
        </div>

        {/* ── LISTA ── */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            padding: 20,
            border: "1px solid #E5E7EB",
            boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
              Solicitações {filtro !== "TODOS" ? `— ${stats.find((s) => s.key === filtro)?.label}` : ""}
            </div>
            <div style={{ fontSize: 12, color: "#9CA3AF" }}>
              {feriasFiltradas.length} registo{feriasFiltradas.length !== 1 ? "s" : ""}
            </div>
          </div>

          {loading ? (
            <>{[1, 2, 3].map((i) => <SkeletonCard key={i} />)}</>
          ) : feriasFiltradas.length === 0 ? (
            <div style={{ padding: "48px 24px", textAlign: "center" }}>
              <InboxOutlined style={{ fontSize: 40, color: "#9CA3AF", marginBottom: 12 }} />
              <div style={{ fontSize: 15, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
                Nenhuma solicitação encontrada
              </div>
              <div style={{ fontSize: 13, color: "#9CA3AF" }}>
                Clique em "Nova Solicitação" para pedir férias
              </div>
            </div>
          ) : (
            feriasFiltradas.map((f) => {
              const cfg = getStatusCfg(f.aprovado);
              const dI = dayjs(f.inicio),
                dF = dayjs(f.fim);
              const dias = dF.diff(dI, "day") + 1;
              return (
                <div
                  key={f.id}
                  className="ferias-card"
                  onClick={() => {
                    setFeriaSelecionada(f);
                    setModalDetalheOpen(true);
                  }}
                  style={{
                    cursor: "pointer",
                    background: "#fff",
                    border: "1px solid #E5E7EB",
                    borderRadius: 14,
                    padding: "18px 20px",
                    marginBottom: 10,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                    borderLeft: `4px solid ${cfg.dot}`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: 12,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", marginBottom: 3 }}>
                        <CalendarOutlined style={{ marginRight: 6, color: cfg.dot }} />
                        Férias — {dias} dia{dias !== 1 ? "s" : ""}
                      </div>
                      <div style={{ fontSize: 12, color: "#9CA3AF" }}>
                        Solicitação #{f.id} &middot; {f.colaborador}
                      </div>
                    </div>
                    <Tag
                      color={cfg.tagColor}
                      style={{ fontWeight: 700, fontSize: 11, letterSpacing: "0.3px", textTransform: "uppercase" }}
                    >
                      {cfg.label}
                    </Tag>
                  </div>
                  <div style={{ display: "flex", gap: 20, fontSize: 12, color: "#6B7280", flexWrap: "wrap" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <CalendarOutlined style={{ fontSize: 13 }} />
                      {dI.format("DD/MM/YYYY")} → {dF.format("DD/MM/YYYY")}
                    </span>
                    {f.aprovado === "S" && f.aprovador && (
                      <span style={{ display: "flex", alignItems: "center", gap: 5, color: "#065F46" }}>
                        <CheckCircleFilled /> {f.aprovador}
                        {f.data_aprovacao && (
                          <span style={{ color: "#9CA3AF" }}> em {dayjs(f.data_aprovacao).format("DD/MM/YYYY")}</span>
                        )}
                      </span>
                    )}
                    {f.obs && (
                      <span style={{ display: "flex", alignItems: "center", gap: 5, color: "#9CA3AF", fontStyle: "italic" }}>
                        "{f.obs.length > 40 ? f.obs.substring(0, 40) + "..." : f.obs}"
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* ══ MODAL NOVA SOLICITAÇÃO ══ */}
        <Modal
          open={modalOpen}
          onCancel={() => setModalOpen(false)}
          footer={null}
          width={480}
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  background: "#DBEAFE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CalendarOutlined style={{ color: "#185FA5", fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>Nova Solicitação de Férias</div>
                <div style={{ fontSize: 12, color: "#9CA3AF", fontWeight: 400 }}>Preencha os dados abaixo</div>
              </div>
            </div>
          }
        >
          <div style={{ paddingTop: 12 }}>
            <Form form={form} layout="vertical" size="middle">
              <Form.Item
                label={<span style={{ fontSize: 12, color: "#6B7280", fontWeight: 600 }}>Período de férias</span>}
                name="periodo"
                rules={[{ required: true, message: "Selecione o período" }]}
              >
                <RangePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder={["Data de início", "Data de fim"]}
                  disabledDate={(current) => current && current < dayjs().startOf("day")}
                />
              </Form.Item>

              <Form.Item
                label={<span style={{ fontSize: 12, color: "#6B7280", fontWeight: 600 }}>Observação (opcional)</span>}
                name="obs"
              >
                <textarea
                  rows={3}
                  placeholder="Alguma nota ou informação adicional..."
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: 8,
                    fontSize: 13,
                    border: "1px solid #D1D5DB",
                    outline: "none",
                    color: "#374151",
                    boxSizing: "border-box",
                    resize: "vertical",
                    fontFamily: "inherit",
                  }}
                />
              </Form.Item>

              <Form.Item noStyle shouldUpdate>
                {() => {
                  const periodo = form.getFieldValue("periodo");
                  if (!periodo || !periodo[0] || !periodo[1]) return null;
                  const dias = periodo[1].diff(periodo[0], "day") + 1;
                  return (
                    <div
                      style={{
                        background: "#EFF6FF",
                        borderRadius: 10,
                        padding: "12px 16px",
                        marginBottom: 16,
                        border: "1px solid #BFDBFE",
                      }}
                    >
                      <div style={{ fontSize: 13, color: "#1E40AF", fontWeight: 600 }}>
                        <CalendarOutlined style={{ marginRight: 6 }} />
                        {periodo[0].format("DD/MM/YYYY")} → {periodo[1].format("DD/MM/YYYY")}
                      </div>
                      <div style={{ fontSize: 12, color: "#3B82F6", marginTop: 4 }}>
                        {dias} dia{dias !== 1 ? "s" : ""} de férias solicitado{dias !== 1 ? "s" : ""}
                      </div>
                    </div>
                  );
                }}
              </Form.Item>

              <Button
                type="primary"
                block
                loading={submitting}
                onClick={submitFerias}
                style={{
                  height: 42,
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 700,
                  background: "linear-gradient(135deg,#185FA5,#2563EB)",
                  border: "none",
                  letterSpacing: "0.2px",
                  boxShadow: "0 4px 14px rgba(24,95,165,0.35)",
                }}
              >
                {submitting ? "A enviar..." : "Enviar Solicitação"}
              </Button>
            </Form>
          </div>
        </Modal>

        {/* ══ MODAL DETALHE ══ */}
        <Modal
          open={modalDetalheOpen}
          onCancel={() => setModalDetalheOpen(false)}
          footer={null}
          width={520}
          title={null}
          closable
          maskClosable
        >
          {feriaSelecionada &&
            (() => {
              const cfg = getStatusCfg(feriaSelecionada.aprovado);
              const dI = dayjs(feriaSelecionada.inicio);
              const dF = dayjs(feriaSelecionada.fim);
              const dias = dF.diff(dI, "day") + 1;
              return (
                <div style={{ padding: "8px 0 0" }}>
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
                      <div
                        style={{
                          width: 42,
                          height: 42,
                          borderRadius: 12,
                          background: cfg.bg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {feriaSelecionada.aprovado === "S" ? (
                          <CheckCircleFilled style={{ fontSize: 22, color: "#065F46" }} />
                        ) : feriaSelecionada.aprovado === "N" ? (
                          <ExclamationCircleFilled style={{ fontSize: 22, color: "#991B1B" }} />
                        ) : (
                          <ClockCircleOutlined style={{ fontSize: 22, color: "#92400E" }} />
                        )}
                      </div>
                      <div>
                        <Typography.Title level={4} style={{ margin: 0, fontWeight: 700, color: "#111827" }}>
                          Detalhes das Férias
                        </Typography.Title>
                        <Typography.Text style={{ fontSize: 13, color: "#6B7280" }}>
                          #{feriaSelecionada.id}
                        </Typography.Text>
                      </div>
                    </div>
                    <Divider style={{ margin: "12px 0 0" }} />
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    {/* Geral */}
                    <div
                      style={{
                        background: "#F9FAFB",
                        borderRadius: 12,
                        padding: "16px 18px",
                        border: "1px solid #F3F4F6",
                      }}
                    >
                      <Typography.Text
                        strong
                        style={{
                          fontSize: 13,
                          color: "#6B7280",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          display: "block",
                          marginBottom: 12,
                        }}
                      >
                        Informações Gerais
                      </Typography.Text>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <DetailRow label="Colaborador" icon={<UserOutlined />} value={feriaSelecionada.colaborador} />
                        <DetailRow
                          label="Estado"
                          icon={null}
                          value={<Tag color={cfg.tagColor} style={{ fontWeight: 500 }}>{cfg.label.toUpperCase()}</Tag>}
                        />
                      </div>
                    </div>

                    {/* Período */}
                    <div
                      style={{
                        background: "#F9FAFB",
                        borderRadius: 12,
                        padding: "16px 18px",
                        border: "1px solid #F3F4F6",
                      }}
                    >
                      <Typography.Text
                        strong
                        style={{
                          fontSize: 13,
                          color: "#6B7280",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          display: "block",
                          marginBottom: 12,
                        }}
                      >
                        Período
                      </Typography.Text>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <DetailRow label="Início" icon={<CalendarOutlined />} value={dI.format("DD/MM/YYYY")} />
                        <DetailRow label="Fim" icon={<CalendarOutlined />} value={dF.format("DD/MM/YYYY")} />
                        <DetailRow
                          label="Duração"
                          icon={<ClockCircleOutlined />}
                          value={`${dias} dia${dias !== 1 ? "s" : ""}`}
                        />
                      </div>
                    </div>

                    {/* Aprovação */}
                    {feriaSelecionada.aprovador && feriaSelecionada.aprovador !== "N" && (
                      <div
                        style={{
                          background: "#F9FAFB",
                          borderRadius: 12,
                          padding: "16px 18px",
                          border: "1px solid #F3F4F6",
                        }}
                      >
                        <Typography.Text
                          strong
                          style={{
                            fontSize: 13,
                            color: "#6B7280",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            display: "block",
                            marginBottom: 12,
                          }}
                        >
                          Aprovação
                        </Typography.Text>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          <DetailRow label="Aprovado por" icon={<UserOutlined />} value={feriaSelecionada.aprovador} />
                          {feriaSelecionada.data_aprovacao && (
                            <DetailRow
                              label="Data"
                              icon={<CalendarOutlined />}
                              value={dayjs(feriaSelecionada.data_aprovacao).format("DD/MM/YYYY HH:mm")}
                            />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Observações */}
                    {(feriaSelecionada.obs || feriaSelecionada.obs2) && (
                      <div
                        style={{
                          background: "#F9FAFB",
                          borderRadius: 12,
                          padding: "16px 18px",
                          border: "1px solid #F3F4F6",
                        }}
                      >
                        <Typography.Text
                          strong
                          style={{
                            fontSize: 13,
                            color: "#6B7280",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            display: "block",
                            marginBottom: 12,
                          }}
                        >
                          Observações
                        </Typography.Text>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                          {feriaSelecionada.obs && (
                            <div>
                              <Typography.Text style={{ fontSize: 12, color: "#9CA3AF" }}>
                                Colaborador:
                              </Typography.Text>
                              <div style={{ fontSize: 13, color: "#374151", marginTop: 2, whiteSpace: "pre-wrap" }}>
                                {feriaSelecionada.obs}
                              </div>
                            </div>
                          )}
                          {feriaSelecionada.obs2 && (
                            <div style={{ marginTop: feriaSelecionada.obs ? 6 : 0 }}>
                              <Typography.Text style={{ fontSize: 12, color: "#9CA3AF" }}>
                                Diretor:
                              </Typography.Text>
                              <div style={{ fontSize: 13, color: "#B91C1C", marginTop: 2, whiteSpace: "pre-wrap" }}>
                                {feriaSelecionada.obs2}
                              </div>
                            </div>
                          )}
                            {feriaSelecionada.obs3 && (
                            <div style={{ marginTop: feriaSelecionada.obs ? 6 : 0 }}>
                              <Typography.Text style={{ fontSize: 12, color: "#9CA3AF" }}>
                                Secretariado:
                              </Typography.Text>
                              <div style={{ fontSize: 13, color: "#B91C1C", marginTop: 2, whiteSpace: "pre-wrap" }}>
                                {feriaSelecionada.obs3}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
        </Modal>
      </Content>
    </Layout>
  );
};

export default SolicitacaoFerias;